import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import type {
  CreateLimitationDefinitionBody,
  LimitationDefinition,
  UpdateLimitationDefinitionBody,
} from "src/shared/api-types/limitations";

import {
  archiveLimitation,
  createLimitation,
  getLimitationConfig,
  getLimitations,
  restoreLimitation,
  updateLimitation,
  updateLimitationConfig,
} from "src/api/limitations";
import { captureHandledException } from "src/services/sentry";

interface UseLimitationsReturn {
  limitations: LimitationDefinition[];
  limitationSetsEnabled: boolean;
  isLoading: boolean;
  reload: () => Promise<void>;
  updateSetsEnabled: (enabled: boolean) => Promise<void>;
  create: (body: CreateLimitationDefinitionBody, options?: { reload?: boolean }) => Promise<void>;
  update: (limitationId: number, body: UpdateLimitationDefinitionBody) => Promise<void>;
  archive: (limitationId: number) => Promise<void>;
  unarchive: (limitationId: number) => Promise<void>;
  reorder: (orderedLimitations: LimitationDefinition[]) => Promise<void>;
}

interface UseLimitationsOptions {
  enabled?: boolean;
}

export function useLimitations(crewId: number, options: UseLimitationsOptions = {}): UseLimitationsReturn {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { enabled = true } = options;
  const [limitations, setLimitations] = useState<LimitationDefinition[]>([]);
  const [limitationSetsEnabled, setLimitationSetsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(enabled);

  const reload = useCallback(async () => {
    if (!enabled) {
      setLimitations([]);
      setLimitationSetsEnabled(false);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const [loadedLimitations, config] = await Promise.all([
        getLimitations({ crewId }),
        getLimitationConfig({ crewId }),
      ]);
      setLimitations(loadedLimitations);
      setLimitationSetsEnabled(config.limitationSetsEnabled);
    } catch (err) {
      captureHandledException(err, { feature: "limitations", action: "load", crewId });
      enqueueSnackbar(t("limitations.load_failed"), { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  }, [crewId, enabled, enqueueSnackbar, t]);

  const updateSetsEnabled = useCallback(
    async (enabled: boolean) => {
      const previous = limitationSetsEnabled;
      setLimitationSetsEnabled(enabled);
      try {
        const config = await updateLimitationConfig({ crewId }, { limitationSetsEnabled: enabled });
        setLimitationSetsEnabled(config.limitationSetsEnabled ?? enabled);
      } catch (err) {
        setLimitationSetsEnabled(previous);
        captureHandledException(err, { feature: "limitations", action: "updateConfig", crewId });
        enqueueSnackbar(t("limitations.update_config_failed"), { variant: "error" });
      }
    },
    [crewId, enqueueSnackbar, limitationSetsEnabled, t],
  );

  useEffect(() => {
    reload();
  }, [reload]);

  const create = useCallback(
    async (body: CreateLimitationDefinitionBody, options: { reload?: boolean } = {}) => {
      await createLimitation({ crewId }, body);
      enqueueSnackbar(t("limitations.created"), { variant: "success", preventDuplicate: true });
      if (options.reload !== false) await reload();
    },
    [crewId, enqueueSnackbar, reload, t],
  );

  const update = useCallback(
    async (limitationId: number, body: UpdateLimitationDefinitionBody) => {
      await updateLimitation({ crewId, limitationId }, body);
      enqueueSnackbar(t("limitations.updated"), { variant: "success" });
      await reload();
    },
    [crewId, enqueueSnackbar, reload, t],
  );

  const archive = useCallback(
    async (limitationId: number) => {
      await archiveLimitation({ crewId, limitationId });
      enqueueSnackbar(t("limitations.archived"), { variant: "success" });
      await reload();
    },
    [crewId, enqueueSnackbar, reload, t],
  );

  const unarchive = useCallback(
    async (limitationId: number) => {
      await restoreLimitation({ crewId, limitationId });
      enqueueSnackbar(t("limitations.unarchived"), { variant: "success" });
      await reload();
    },
    [crewId, enqueueSnackbar, reload, t],
  );

  const reorder = useCallback(
    async (orderedLimitations: LimitationDefinition[]) => {
      await Promise.all(
        orderedLimitations.map((limitation, index) =>
          updateLimitation({ crewId, limitationId: limitation.id }, { sortOrder: index }),
        ),
      );
      setLimitations(
        orderedLimitations
          .map((limitation, index) => ({ ...limitation, sortOrder: index }))
          .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id),
      );
    },
    [crewId],
  );

  return {
    limitations,
    limitationSetsEnabled,
    isLoading,
    reload,
    updateSetsEnabled,
    create,
    update,
    archive,
    unarchive,
    reorder,
  };
}
